import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes text by removing diacritics and converting to lowercase.
 *
 * Defined here (not in helpers/functions.ts) to keep @/lib/utils free from
 * service imports — helpers/functions.ts imports filters-services which
 * extends ApiService, breaking SSR in Server Components.
 */
export function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Creates a regex safe version of a string by escaping special characters
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Prepares text for highlighting by wrapping matches in special delimiter tokens
 * that can be replaced with HTML spans when rendering
 *
 * @param text The original text to search within
 * @param query The search query to highlight
 * @returns Text with delimiters around matched parts
 */
export function prepareHighlightedText(text: string, query: string): string {
  if (!query || !text) return text;

  // Normalize both the text and the query for comparison
  const normalizedText = normalizeText(text);
  const normalizedQuery = normalizeText(query);

  // Escape regex characters in the normalized query
  const escapedQuery = escapeRegExp(normalizedQuery);
  const regex = new RegExp(escapedQuery, 'gi'); // 'g' for global, 'i' for case-insensitive

  let result = '';
  let lastIndex = 0;
  let match;

  // Iterate over all matches in the normalized text
  while ((match = regex.exec(normalizedText)) !== null) {
    const startIndex = match.index;
    // The length of the match in the original text might differ if the query had accents removed
    // Use the length of the original query portion that corresponds to the normalized match
    const matchLength = query.length; // Use original query length for slicing original text

    // Append the text before the match (from original text)
    result += text.substring(lastIndex, startIndex);
    // Append the matched part (from original text), wrapped in delimiters
    result += `|||${text.substring(startIndex, startIndex + matchLength)}|||`;
    // Update the last index
    lastIndex = startIndex + matchLength;
  }

  // Append any remaining text after the last match (from original text)
  result += text.substring(lastIndex);

  // Handle case where nothing was matched - return original text
  if (lastIndex === 0) {
    return text;
  }

  return result;
}

type LegislationSegment = {
  key: string;
  value: string;
  displayValue?: string;
};

interface LegalElementConfig {
  fullName: string;
  abbreviation: string;
  priority: number; // Lower number = higher priority in titles
}

// Slovak legal document structure mapping
const LEGAL_ELEMENTS: Record<string, LegalElementConfig> = {
  // Main structural elements
  clanok: { fullName: 'Článok', abbreviation: 'Čl.', priority: 1 },
  paragraf: { fullName: 'Paragraf', abbreviation: '§', priority: 1 },

  // Sub-structural elements
  odsek: { fullName: 'Odsek', abbreviation: 'ods.', priority: 2 },
  odstavec: { fullName: 'Odstavec', abbreviation: 'odst.', priority: 2 },

  // Detailed elements
  pismeno: { fullName: 'Písmeno', abbreviation: 'písm.', priority: 3 },
  bod: { fullName: 'Bod', abbreviation: 'bod', priority: 4 },

  // Special elements
  cast: { fullName: 'Časť', abbreviation: 'časť', priority: 0 },
  hlava: { fullName: 'Hlava', abbreviation: 'hlava', priority: 0 },
  diel: { fullName: 'Diel', abbreviation: 'diel', priority: 1 },
  oddiel: { fullName: 'Oddiel', abbreviation: 'oddiel', priority: 1 },

  // Content types
  text: { fullName: 'Text', abbreviation: 'text', priority: 10 },
  nadpis: { fullName: 'Nadpis', abbreviation: 'nadpis', priority: 10 },
  poznamka: { fullName: 'Poznámka', abbreviation: 'pozn.', priority: 10 },
};

// Enhanced parsing function
function parseLegislationFragment(fragment: string): LegislationSegment[] {
  return fragment
    .split('.')
    .map((part) => {
      const [rawKey, ...rest] = part.split('-');
      const value = rest.join('-');

      if (!rawKey || !value) return null;

      const key = rawKey.toLowerCase();
      const config = LEGAL_ELEMENTS[key];

      // Format display value based on content type
      let displayValue = value;
      if (config && isNumericValue(value)) {
        displayValue = value;
      } else if (config && isAlphaValue(value)) {
        displayValue = value.toUpperCase();
      }

      return {
        key,
        value,
        displayValue,
        config,
      } as LegislationSegment & { config?: LegalElementConfig };
    })
    .filter(
      (s): s is LegislationSegment & { config?: LegalElementConfig } =>
        s !== null
    );
}

// Helper functions
function isNumericValue(value: string): boolean {
  return /^\d+$/.test(value);
}

function isAlphaValue(value: string): boolean {
  return /^[a-zA-Z]$/.test(value);
}

// Main enhanced function
function formatNestedLegislationCitation(
  hrefOrFragment: string,
  options: {
    useAbbreviations?: boolean;
    maxElements?: number;
    separator?: string;
    includeUnknown?: boolean;
  } = {}
): string | null {
  const {
    useAbbreviations = true,
    maxElements = 4,
    separator = ' ',
    includeUnknown = true,
  } = options;

  const fragment = hrefOrFragment.includes('#')
    ? hrefOrFragment.split('#')[1]!
    : hrefOrFragment;

  if (!fragment) return null;

  // Handle citations (cross-references)
  const [baseFrag, ...citations] = fragment.split('.citat');
  const baseSegs = parseLegislationFragment(baseFrag ?? '') as Array<
    LegislationSegment & { config?: LegalElementConfig }
  >;

  // If any segment is unknown (no config), return null to show link icon instead
  // This prevents ugly raw text like "Prilohy ... Op legislativne_pravidla..."
  const hasUnknownSegments = baseSegs.some((seg) => !seg.config);
  if (hasUnknownSegments && !includeUnknown) {
    return null;
  }

  // Sort segments by priority (lower number = higher priority)
  const sortedSegs = baseSegs
    .filter((seg) => seg.config || includeUnknown)
    .sort((a, b) => {
      const aPriority = a.config?.priority ?? 999;
      const bPriority = b.config?.priority ?? 999;
      return aPriority - bPriority;
    })
    .slice(0, maxElements);

  // Build the base title
  const baseParts = sortedSegs.map((seg) => {
    if (seg.config) {
      const label = useAbbreviations
        ? seg.config.abbreviation
        : seg.config.fullName;
      return `${label} ${seg.displayValue}`;
    } else {
      // Handle unknown elements gracefully
      const capitalizedKey = seg.key.charAt(0).toUpperCase() + seg.key.slice(1);
      return `${capitalizedKey} ${seg.displayValue}`;
    }
  });

  let title = baseParts.join(separator);

  // Handle citations if present
  if (citations.length > 0) {
    const citationTitles = citations
      .map((citation) => {
        const citationFrag = citation.replace(/^\./, '');
        const citeSegs = parseLegislationFragment(citationFrag) as Array<
          LegislationSegment & { config?: LegalElementConfig }
        >;

        // For citations, prefer the most significant element
        const mainCiteElement = citeSegs
          .filter((seg) => seg.config)
          .sort((a, b) => a.config!.priority - b.config!.priority)[0];

        if (mainCiteElement) {
          const label = useAbbreviations
            ? mainCiteElement.config!.abbreviation
            : mainCiteElement.config!.fullName;
          return `${label} ${mainCiteElement.displayValue}`;
        }

        return null;
      })
      .filter(Boolean);

    if (citationTitles.length > 0) {
      title += ` – ${citationTitles.join(', ')}`;
    }
  }

  return title || null;
}

// Additional utility function for different formatting styles
export function formatLegalReference(
  hrefOrFragment: string,
  style: 'formal' | 'abbreviated' | 'minimal' = 'abbreviated'
): string | null {
  const styleConfigs = {
    formal: {
      useAbbreviations: false,
      maxElements: 6,
      separator: ', ',
      includeUnknown: true,
    },
    abbreviated: {
      useAbbreviations: true,
      maxElements: 4,
      separator: ' ',
      includeUnknown: false, // Return null for unknown segments → show link icon
    },
    minimal: {
      useAbbreviations: true,
      maxElements: 2,
      separator: ' ',
      includeUnknown: false,
    },
  };

  return formatNestedLegislationCitation(hrefOrFragment, styleConfigs[style]);
}
